import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import Color from '@/Enums/Color.enum';
import { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import { ICampaignManagementCampaign } from '@/Interfaces/CampaignManagement/Campaign.interface';
import type { ICampaignFormData } from '@/Interfaces/CampaignManagement/CampaignForm.interface';
import { ICampaignManagementAdvertiserControlOption } from '@/Interfaces/ControlOption.interface';
import { IDV360TicketsInsertionOrder } from '@/Interfaces/DV360Tickets/InsertionOrder.interface';
import TypeIcon from '@/components/Icons/TypeIcon';
import InputSkeleton from '@/components/Skeleton/InputSkeleton';
import { campaignApiDataToFormStructure } from '@/helpers/campaignManagement/campaignForm';
import { insertionOrderApiDataToFormStructure } from '@/helpers/dv360Tickets/campaignManagement/insertionOrderForm';
import { getFieldErrorProps } from '@/helpers/form';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import AdvertiserModal from '@/pages/Advertisers/AdvertiserModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectTotalImpressionsBookedForActiveCampaign } from '@/store/selectors/campaignManagement.selectors';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { selectDV360TicketsActiveInsertionOrder } from '@/store/selectors/dv360Tickets/insertionOrders.selectors';
import {
  selectGoogleAdvertisers,
  selectOmnichannelAdvertisers,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import { getAdvertiserCategories } from '@/store/slices/advertisers.slice';
import { getClients } from '@/store/slices/sharedApi.slice';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Button, ListItemIcon, MenuItem, OutlinedInput, Paper, Select, TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { defaultFormData } from './constants';
import { campaignFormValidationSchema } from './validationSchema';

interface IProps {
  isNew: boolean;
  onSave: (formValues: ICampaignFormData, platform: CampaignPlatformType) => Promise<void>;
  readOnly: boolean;
  loading: boolean;
  isDV360: boolean;
}

const labelClassname = 'text-sm text-[#1E1E1E] font-semibold mb-2 whitespace-nowrap';

const CampaignForm = ({ isNew, onSave, readOnly, loading, isDV360 }: IProps) => {
  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    resolver: yupResolver(campaignFormValidationSchema),
    defaultValues: defaultFormData,
    mode: 'onChange',
  });

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isAdvertiserModalOpen, setIsAdvertiserModalOpen] = useState(false);
  const [platformType, setPlatformType] = useState<CampaignPlatformType>(
    isDV360 ? CampaignPlatformType.Google : CampaignPlatformType.Omnichannel,
  );

  const activeBWCampaign = useAppSelector(({ campaignManagement }) =>
    isNew ? null : campaignManagement.activeCampaign,
  ) as ICampaignManagementCampaign | null;

  const { activeInsertionOrder } = useAppSelector(selectDV360TicketsActiveInsertionOrder);

  const activeCampaign = useMemo(() => {
    return isDV360 ? activeInsertionOrder : activeBWCampaign;
  }, [isDV360, activeBWCampaign, activeInsertionOrder]);

  const totalImpressions = useAppSelector(selectTotalImpressionsBookedForActiveCampaign);
  const isDV360CampaignManagementEnabled = useAppSelector(selectIsDV360CampaignManagementEnabled);

  useEffect(() => {
    dispatch(getClients({ refresh: true }));
    dispatch(getAdvertiserCategories());
  }, [dispatch]);

  useEffect(() => {
    if (activeCampaign) {
      const formData = isDV360
        ? insertionOrderApiDataToFormStructure(activeCampaign as IDV360TicketsInsertionOrder)
        : campaignApiDataToFormStructure(activeCampaign as ICampaignManagementCampaign);
      reset(formData);
    }
  }, [activeCampaign, reset, isDV360]);

  const omnichannelAdvertisers = useAppSelector(selectOmnichannelAdvertisers);
  const googleAdvertisers = useAppSelector(selectGoogleAdvertisers);

  const advertisers = useMemo<IParsedAdvertiser<number>[]>(() => {
    const advertisersByPlatform = {
      [CampaignPlatformType.Omnichannel]: omnichannelAdvertisers,
      [CampaignPlatformType.Google]: googleAdvertisers,
    };
    return (
      advertisersByPlatform[isDV360CampaignManagementEnabled ? platformType : CampaignPlatformType.Omnichannel] ?? []
    ).filter((item): item is IParsedAdvertiser<number> => item !== undefined);
  }, [platformType, omnichannelAdvertisers, googleAdvertisers, isDV360CampaignManagementEnabled]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSave(getValues(), platformType);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdvertiserModalClose = useCallback(
    (shouldRefresh = false) => {
      setIsAdvertiserModalOpen(false);
      if (shouldRefresh) {
        dispatch(getClients({ refresh: true }));
      }
    },
    [dispatch],
  );

  return (
    <>
      <Paper className='p-5 mt-4 sticky top-[-100px] z-20'>
        <form className='flex flex-col' onSubmit={handleSubmit(handleSave)}>
          <div className='flex items-center gap-4 justify-between sticky z-20 top-14 bg-white'>
            <div className='flex flex-1 flex-col'>
              <label htmlFor='campaign-name' className={labelClassname}>
                Campaign Name *
              </label>
              {loading ? (
                <InputSkeleton />
              ) : (
                <Controller
                  name='name'
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      id='campaign-name'
                      fullWidth
                      disabled={readOnly}
                      placeholder='Enter Campaign Name'
                      {...getFieldErrorProps(fieldState)}
                    />
                  )}
                />
              )}
            </div>
            {isDV360CampaignManagementEnabled && (
              <div className='flex flex-1 flex-col'>
                <label htmlFor='campaign-platform' className={labelClassname}>
                  Platform *
                </label>
                {loading ? (
                  <InputSkeleton />
                ) : (
                  <Select
                    onChange={e => setPlatformType(e.target.value as CampaignPlatformType)}
                    input={<OutlinedInput className='h-full' />}
                    renderValue={value => (
                      <span>
                        <span className='flex flex-direction items-center'>
                          <ListItemIcon className='mr-[-28px]'>
                            <TypeIcon />
                          </ListItemIcon>
                          <span className='text-[#1B4332] text-base font-semibold'>{value}</span>
                        </span>
                      </span>
                    )}
                    value={platformType}
                    disabled={readOnly || !isNew}
                  >
                    {Object.values(CampaignPlatformType).map(platform => (
                      <MenuItem key={platform} value={platform}>
                        {platform}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </div>
            )}
            <div className='flex flex-1 flex-col'>
              <label htmlFor='campaign-advertiser' className={labelClassname}>
                Advertiser *
              </label>
              {loading ? (
                <InputSkeleton />
              ) : (
                <Controller
                  name='advertiser'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete<ICampaignManagementAdvertiserControlOption>
                      {...field}
                      id='campaign-advertiser'
                      disabled={readOnly || !isNew}
                      renderInput={props => (
                        <TextField
                          {...props}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: '400',
                            },
                          }}
                          placeholder='Select Advertiser'
                          {...getFieldErrorProps(fieldState)}
                        />
                      )}
                      getOptionLabel={option => `${option.label} (ID: ${option.beeswaxId ?? 'N/A'})`}
                      getOptionKey={option => option.value}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(_, value) => field.onChange(value)}
                      options={advertisers}
                      slots={{
                        paper: ({ children }) => (
                          <div style={{ backgroundColor: '#fff' }}>
                            {children}
                            <div style={{ padding: '8px', textAlign: 'center' }}>
                              <Button
                                sx={{ height: 40, width: '100%' }}
                                color='primary'
                                variant='contained'
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => setIsAdvertiserModalOpen(true)}
                              >
                                + Create new Advertiser
                              </Button>
                            </div>
                          </div>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </div>
            <div className='flex flex-1 flex-col'>
              <label htmlFor='campaign-billing-id' className={labelClassname}>
                External ID / Billing ID
              </label>
              {loading ? (
                <InputSkeleton />
              ) : (
                <Controller
                  name='altId'
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id='campaign-billing-id'
                      disabled={readOnly}
                      placeholder='Enter External ID / Billing ID'
                      {...getFieldErrorProps(fieldState)}
                    />
                  )}
                />
              )}
            </div>
          </div>
          <div className='mt-4 flex justify-between items-end'>
            {loading ? (
              <InputSkeleton sx={{ width: 100 }} />
            ) : (
              <span
                className={labelClassname}
              >{`Total impressions booked: ${numberToUsFormat(totalImpressions)}`}</span>
            )}
            <Button
              loading={isLoading}
              loadingPosition='end'
              type='submit'
              variant='contained'
              sx={{ height: '48px', width: '130px', background: Color.Primary }}
              disabled={!isValid || !isDirty || readOnly}
            >
              Save
            </Button>
          </div>
        </form>
      </Paper>
      <AdvertiserModal
        open={isAdvertiserModalOpen}
        onClose={handleAdvertiserModalClose}
        loading={isLoading}
        setLoading={setIsLoading}
      />
    </>
  );
};

export default CampaignForm;
